import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { CheckCircle2, Clock, Phone, ArrowLeft, Mail } from "lucide-react";
import { Button } from "../components/ui/button";
import { company } from "../mock";
import useSeo from "../hooks/useSeo";
import { reportConversion, gtagEvent } from "../lib/gtag";

export default function ThankYou() {
  const location = useLocation();
  const name = location.state?.name;

  useSeo({
    title: "Thank You | Elite Vending",
    description: "Thanks for your request — the Elite Vending team will be in contact within 24 hours.",
  });

  // Measure the conversion as a Thank You page view.
  useEffect(() => {
    // Register the SPA page view with the Google tag...
    gtagEvent("page_view", { page_path: "/thank-you", page_title: "Thank You" });
    // ...and fire the Google Ads lead conversion (active once the "lead"
    // label is set in src/lib/gtag.js).
    reportConversion("lead", { source: location.state?.source || "form" });
  }, [location.state]);

  return (
    <main className="pt-20">
      <section className="relative bg-ink overflow-hidden">
        <div className="pointer-events-none absolute -top-24 -right-24 h-96 w-96 rounded-full bg-gold/15 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-24 h-80 w-80 rounded-full bg-gold/10 blur-3xl" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 text-center">
          <span className="mx-auto grid place-items-center h-20 w-20 rounded-full bg-gold text-ink shadow-[0_10px_30px_rgba(201,162,74,0.5)]">
            <CheckCircle2 size={44} />
          </span>

          <h1 className="mt-7 font-display text-4xl sm:text-5xl text-white leading-tight">
            Thank you{name ? `, ${name.split(" ")[0]}` : ""}!
          </h1>

          <p className="mt-4 text-lg text-white/70 max-w-xl mx-auto leading-relaxed">
            Your request has been received. A member of the Elite Vending team will
            be in contact with you <span className="text-gold font-semibold">within 24 hours</span> to
            confirm your details and arrange your free site visit.
          </p>

          <div className="mt-8 inline-flex items-center gap-2.5 rounded-full border border-gold/40 bg-white/5 px-5 py-2.5 text-sm font-medium text-white">
            <Clock size={16} className="text-gold" /> Expect to hear from us within one business day
          </div>

          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <a href={company.phoneHref}>
              <Button className="h-12 px-8 rounded-full bg-gold hover:bg-gold-dark text-ink font-semibold text-base">
                <Phone size={18} className="mr-2" /> Call us now: {company.phone}
              </Button>
            </a>
            <Link to="/">
              <Button variant="outline" className="h-12 px-8 rounded-full border-white/40 text-white bg-transparent hover:bg-white hover:text-ink font-semibold text-base">
                <ArrowLeft size={18} className="mr-2" /> Back to home
              </Button>
            </Link>
          </div>

          <p className="mt-8 flex items-center justify-center gap-2 text-sm text-white/50">
            <Mail size={15} className="text-gold" /> Prefer email? Reach us at {company.email}
          </p>
        </div>
      </section>

      {/* What happens next */}
      <section className="bg-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl sm:text-3xl text-ink text-center">What happens next</h2>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              { n: 1, t: "We review your details", d: "We check your site info and confirm you qualify for a free machine." },
              { n: 2, t: "We call within 24 hours", d: "A friendly team member reaches out to arrange your free site visit." },
              { n: 3, t: "We get you set up", d: "Choose your products and we deliver, install and stock — often within days." },
            ].map((s) => (
              <div key={s.n} className="rounded-2xl border border-gold/20 bg-gold-soft/40 p-6 text-center">
                <span className="mx-auto grid place-items-center h-11 w-11 rounded-full bg-ink text-gold font-display text-lg">{s.n}</span>
                <h3 className="mt-4 font-display text-lg text-ink">{s.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
